import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { motion } from 'framer-motion';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Validate that we have a token
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axiosInstance.post('/auth/reset-password', {
        token,
        password
      });

      setMessage(response.data.message);
      
      // Store the access token and redirect to dashboard
      if (response.data.accessToken) {
        localStorage.setItem('accessToken', response.data.accessToken);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred while resetting your password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-hidden bg-[#f8fafc] relative" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Animated background - same as login/signup */}
      <div className="absolute inset-0 z-0">
        {/* Radial gradient: edges 8979FF → FFC9A4 → white (darker at sides) */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 85% 85% at 50% 50%, #ffffff 0%, rgba(255,255,255,0.97) 28%, rgba(255,201,164,0.2) 52%, rgba(137,121,255,0.25) 78%, rgba(137,121,255,0.3) 100%)',
          }}
        />
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-[#8979FF]/10 rounded-full blur-3xl"
          animate={{ x: [0, 50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFC9A4]/10 rounded-full blur-3xl"
          animate={{ x: [0, -40, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle, #8979FF 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            backgroundPosition: '0 0',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="bg-white p-8 rounded-xl border border-[#DDE2EE] max-w-md w-full shadow-sm" style={{ fontFamily: 'Raleway, sans-serif' }}>
          <div className="text-center mb-8">
            <h1 className="text-3xl font-medium text-[#0f172a] mb-2">Reset Password</h1>
            <p className="text-sm text-[#475569]">Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#475569] mb-2">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                placeholder="Enter new password"
                required
                minLength={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#475569] mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                placeholder="Confirm new password"
                required
                minLength={8}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 border border-red-200 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {message && (
              <div className="bg-green-50 text-green-700 border border-green-200 p-3 rounded-lg text-sm">
                {message}
                {message.includes('successfully') && (
                  <div className="mt-2 text-xs text-[#475569]">Redirecting to dashboard...</div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 rounded-lg font-medium bg-[#8979FF] text-white hover:bg-[#7c6ae8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#8979FF]"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-[#475569] hover:text-[#8979FF] transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;